/**
 * POST-INSTALL-02 — "what emails have been sent to customers" (Seth
 * 2026-08-21), on the job's Post-Install tab. Reads the SAME outbox the
 * comms engine writes: sent = actually delivered; held = written but parked
 * behind comms_live=false; scheduled = queued for a future date.
 */
import { useQuery } from "@tanstack/react-query";
import { Mail, MessageSquare } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";

interface OutboxRow {
  id: string;
  channel: string | null;
  to_address: string | null;
  subject: string | null;
  status: string;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string;
  automation_key: string | null;
}

const STATUS_CHIP: Record<string, string> = {
  sent: "bg-emerald-50 text-emerald-700",
  held: "bg-amber-50 text-amber-700",
  scheduled: "bg-sky-50 text-sky-700",
  cancelled: "bg-slate-100 text-slate-400",
  failed: "bg-rose-50 text-rose-600",
};

function useCustomerComms(custno: string | null) {
  return useQuery({
    queryKey: ["customer_comms", custno],
    enabled: !!custno,
    queryFn: async (): Promise<OutboxRow[]> => {
      const { data, error } = await supabase.from("pegasus_outbox")
        .select("id, channel, to_address, subject, status, scheduled_for, sent_at, created_at, automation_key")
        .eq("custno", custno!)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as OutboxRow[];
    },
  });
}

export function SentComms({ custno }: { custno: string | null }) {
  const { data: rows } = useCustomerComms(custno);
  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-slate-800">
        <Mail className="h-[18px] w-[18px] text-brand-500" /> Customer Emails &amp; Texts
      </h3>
      {(rows ?? []).length === 0 ? (
        <p className="rounded-lg border border-dashed border-hairline py-5 text-center text-xs text-slate-400">
          Nothing queued or sent to this customer yet — post-install sends will land here.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {(rows ?? []).map((r) => (
            <li key={r.id} className="flex items-center gap-2 py-2 text-sm">
              {r.channel === "sms"
                ? <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                : <Mail className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-slate-700">
                  {r.subject || r.automation_key || (r.channel === "sms" ? "Text message" : "Email")}
                </span>
                <span className="block truncate text-[11px] text-slate-400">{r.to_address ?? ""}</span>
              </span>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_CHIP[r.status] ?? "bg-slate-100 text-slate-500")}>
                {r.status === "sent" ? `Sent ${fmt(r.sent_at)}`
                  : r.status === "held" ? "Held (comms off)"
                  : r.status === "scheduled" ? `Scheduled ${fmt(r.scheduled_for)}`
                  : r.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
